// Security as the Application API serves it (`GET /api/security`): how the person signs in, their sessions of the last
// 30 days, and for an Owner LAMP support's Break-glass Access; and signing out everywhere else. Dates are ISO 8601.
// The External Assistants holding access are the Connections page's (`api.connections()`), revoked as there.
import { ApiError, call, type Fetch } from "../http.js";
import type { PinConsentGiven } from "../sign-in/api.js";

export type SignInMethod = "email" | "telegram" | "sms";
export type BreakGlassAction = "BreakGlassOpened" | "BreakGlassUsed" | "BreakGlassClosed";

export interface SessionRow {
  /** The session of this browser. */
  readonly current: boolean;
  /** On a Trusted Device: kept 30 days. */
  readonly trusted: boolean;
  readonly startedAt: string;
  readonly expiresAt: string;
  readonly endedAt: string | null;
  readonly live: boolean;
}

export interface BreakGlassRow {
  readonly action: BreakGlassAction;
  readonly at: string;
  readonly basis: "owner_consent" | "declared_incident" | null;
  readonly reason: string | null;
  readonly until: string | null;
}

export interface SecurityView {
  readonly methods: readonly SignInMethod[];
  readonly pin: boolean;
  readonly sessions: readonly SessionRow[];
  /** Null unless the person is an Owner. */
  readonly breakGlass: readonly BreakGlassRow[] | null;
}

export interface SecurityApi {
  view(): Promise<SecurityView>;
  /** Ends the person's other sessions; how many ended. */
  signOutEverywhere(): Promise<number>;
  /**
   * Sets the person's PIN, after a `pin.change` step-up. "consent_required" when their consent to LAMP keeping it
   * does not stand and none was confirmed with it: ask, and set it again with the consent (#698).
   */
  setPin(pin: string, consent?: PinConsentGiven): Promise<"set" | "consent_required">;
}

export const createSecurityApi = (fetcher: Fetch): SecurityApi => ({
  view: () => call<SecurityView>(fetcher, "/api/security"),
  signOutEverywhere: async () =>
    (await call<{ ended: number }>(fetcher, "/api/security/sign-out-everywhere", { json: {} })).ended,
  setPin: async (pin, consent) => {
    try {
      await call(fetcher, "/auth/pin/set", { json: { pin, ...(consent ? { consent } : {}) } });
      return "set";
    } catch (error) {
      if (error instanceof ApiError && error.status === 403 && error.code === "consent_required")
        return "consent_required";
      throw error;
    }
  },
});
